import type { Category, ContextNode, Game, Profile } from "@/domain/types";
import defaultCvars from "./Destiny2_default.cvars.xml" with { type: "text" };
import { edennilDestiny } from "./edennil";
import { ingestDestiny } from "./ingest";

// Destiny 2's two flat Contexts: the same physical keys mean different things on foot vs
// in a vehicle (W is the Kinetic weapon on foot, Dodge Left in a Sparrow), so they cannot share one
// resolved layer - a plain selector picks which. Destiny is the first real Game.contexts user.
export const DESTINY2_CONTEXTS: ContextNode[] = [
  // id stays the stable slug "on-foot"; the display label is "Guardian" (the Destiny term)
  { id: "on-foot", label: "Guardian" },
  { id: "vehicle", label: "Vehicle" },
];

// Categories share one palette with Deadlock so the same kind of action reads the same hue in both
// games (Abilities red 25, the fight category gold 85, Interface cyan 205, Movement blue 265), every
// pair in a panel kept >= 60 degrees apart so nothing bleeds. Vehicle (green 145, Vehicle Context only)
// and the grey/null Utility round it out.
export const DESTINY2_CATEGORIES: Record<string, Category> = {
  movement: { label: "Movement", hue: 265 },
  weapon: { label: "Weapons", hue: 85 },
  ability: { label: "Abilities", hue: 25 },
  utility: { label: "Utility", hue: null },
  vehicle: { label: "Vehicle", hue: 145 },
  interface: { label: "Interface", hue: 205 },
};

// The built-in Default profile, parsed from Bungie's stock cvars.xml - a freshly factory-reset export
// (Settings > Key Mapping reverted to default), vendored byte-faithfully in ./sources. Both Destiny
// Profiles are now file-derived through the one shared ingest (no hand-authoring): the Default from
// the stock file, the Edennil setup from the maintainer's file. Board-portable (no pin).
//
// The stock default overloads two keys: it binds BOTH Class Ability and Stasis Breakout to V, and BOTH
// Melee and Block to C. No per-setup curation is needed - the universal first-wins + shadowed mechanism
// (decision 0034) keeps the primary (Class Ability, Melee - listed first in DESTINY_BINDS) and records
// the other as its shadow, so the displaced action still surfaces in the readout / bindings card. A
// setup that rebinds them to their own keys (Edennil) shows each outright. (vehicle_zoom shares RMB with
// vehicle_boost and is omitted by the registry.)
const defaultProfile: Profile = {
  ...ingestDestiny(defaultCvars, { id: "default", name: "Default (PC)" }),
  // the stock cvars.xml, offered for download verbatim (the viewer is read-only, so the file IS the
  // setup). The text lives in scope here at assembly time; the ingested Profile keeps none of its own.
  download: { fileName: "Destiny2_default.cvars.xml", text: defaultCvars },
};

export const destiny2: Game = {
  id: "destiny-2",
  name: "Destiny 2",
  categories: DESTINY2_CATEGORIES,
  contexts: DESTINY2_CONTEXTS,
  profiles: [defaultProfile, edennilDestiny],
  // a player's own cvars.xml ingests into a "my own" Profile through the one shared Destiny path -
  // the same ingest the built-ins use, board-portable (no pin). Its presence is what gives the
  // Destiny Profile picker an upload button.
  ingestUpload: ingestDestiny,
  // how that upload presents in the import modal: a Destiny cvars.xml file.
  uploadFormat: {
    accept: ".xml,.txt,text/plain",
    fileLabel: "Destiny cvars.xml",
    pasteLabel: "cvars.xml",
  },
};
